"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NEON_GRID_THEME = void 0;
const common_1 = require("./common");
exports.NEON_GRID_THEME = (0, common_1.firstPartyTheme)({
    id: 'neon-grid',
    name: 'Neon Grid',
    tagline: 'Dark tech: a blueprint grid, electric mint signals and ultraviolet glow for products that ship.',
    description: 'Neon Grid is a dark-first system for developer tools, AI products, SaaS dashboards, crypto and hardware launches. A near-black ground with a faint blueprint grid, electric-mint primary, ultraviolet accent, Space Grotesk headlines, Inter UI text and JetBrains Mono for code, data and labels. Compact density, crisp 4px corners and motion that feels like a terminal coming online.',
    categories: ['tech', 'bold'],
    tokens: {
        colors: {
            background: '#07080c',
            surface: '#0f121a',
            text: '#e6edf7',
            muted: '#8892a6',
            primary: '#3df2c2',
            primaryText: '#04110d',
            accent: '#a86bff',
            border: '#1e2433',
        },
        fonts: { heading: 'Space Grotesk', body: 'Inter', mono: 'JetBrains Mono' },
        radius: 4,
        density: 'compact',
        typeScale: 1.25,
    },
    guide: `## Mood
Mission control at 2 a.m. Precise, fast, a little dangerous. The interface feels like an instrument: every element has a reason, data is visible, and the product is shown working, not described. Dark-first by design; this theme has no light mode.

## Grid and layout
- Draw the grid. A faint 1px line grid (border colour at 40–60% opacity, 64px cells) behind the hero and section headers, fading out with a radial mask. Align content to its lines.
- Bento layouts for features: a 6- or 12-column grid of panels of varied spans (2x1, 1x1, 2x2), each panel on surface with a 1px border and the 4px radius.
- Show the product: real UI screenshots, terminal snippets, code blocks, charts and logs inside panels. Every section should contain at least one artefact of the product.
- Compact density: tighter paddings, more information per screen, but never cramped type.

## Typography
- Space Grotesk headlines, weight 600, tight tracking (-0.02em), the 4xl and 5xl steps for heroes. Keep headlines to one or two lines.
- Inter for body and UI at 14–16px.
- JetBrains Mono for labels, metrics, version numbers, keyboard shortcuts, eyebrow text ("// 01 — DEPLOY"), table data and code. Uppercase mono eyebrows at 11–12px, tracked 0.08em, in the muted or mint colour.

## Colour
- Background near-black, panels slightly lifted. Text is cool off-white; secondary text is slate.
- Electric mint is the signal: primary buttons, live indicators, success states, the active tab, focus rings.
- Ultraviolet is the glow: gradient borders on the featured panel, a soft radial bloom (accent at 15–25% opacity, 400–600px blur) behind the hero, syntax highlights.
- At most one glow per viewport. Never put mint text on violet.

## Imagery
UI and data, not stock photography. Product screenshots framed in panels with a 1px border; abstract 3D or wireframe renders in the palette; no people unless they are real team photos treated in monochrome.

## Motion
Mechanical and quick: 120–200ms for UI, 400–600ms for reveals, ease-out-expo (cubic-bezier(0.16, 1, 0.3, 1)). Typed-out terminal lines, counters ticking up, grid lines drawing in, a scan-line sweep over a panel on first view. Hover: border brightens to the accent, never scales.

## Copy tone
Technical and confident. Short verbs, real numbers, no fluff: "Deploy in 38 seconds", "p99 under 40ms", "Works with your stack". Speak to builders; show the command or the API call. Version labels and changelog links build trust.

## Do
- Include a code or terminal block in the hero or right below it.
- Put real metrics in mono with units.
- Make keyboard shortcuts visible (kbd elements in the mono face).

## Don't
- No light sections, pastel colours or rounded pill buttons.
- No glow on everything; restraint keeps the glow special.
- No vague claims without a number or a demo behind them.`,
    sections: [
        {
            name: 'Hero: command line',
            recipe: 'Dark section over the faint line grid with a violet radial bloom behind the headline. Mono eyebrow with a pulsing mint dot ("● v2.4 is live"), Space Grotesk headline at 5xl, one sentence in muted text, then a mint primary button and a secondary button with a 1px border. Below: a terminal panel (surface, 1px border, three window dots) that types out an install command and its output line by line.',
        },
        {
            name: 'Bento features',
            recipe: 'A 12-column grid of 5–7 panels with mixed spans. Each panel: mono eyebrow, a short Space Grotesk title, one line of text and a live artefact (mini chart, code snippet, toggle, log stream). The featured panel spans 2x2 and has a 1px gradient border from violet to mint. Panels brighten their border to the accent on hover.',
        },
        {
            name: 'Metrics bar',
            recipe: 'A single row of 3–4 metrics separated by vertical 1px lines: the number large in JetBrains Mono (3xl step, mint), the unit and label below in muted 12px mono uppercase. Numbers count up from zero over 900ms when scrolled into view.',
        },
        {
            name: 'Pricing table',
            recipe: 'Three columns on surface panels; the recommended plan has the gradient border and a mint "Recommended" mono tag. Price in Space Grotesk 3xl with a mono "/mo". Feature list uses mint check marks and mono for limits ("10k req/day"). A comparison table below uses mono for every value.',
        },
    ],
});
